import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import OtpInput from 'react-otp-input'
import { useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import queryString from 'query-string'
import cookies from 'lib/cookies'
import useAssert from 'lib/hooks/useAssert'
import { isAllowedRedirectUrl, isBase64Encoded } from 'lib/helpers'
import { logException } from 'lib/errors'
import { getPrimaryLocation } from 'lib/location'
import { getLocale } from 'lib/utils/getLocale'

import Button from 'components/UI/Button'
import Link from 'components/Link'
import { REDIRECT_USER_TYPE } from 'components/User/UserEdit/UserEdit'

import checkVerificationCode from 'graphql/user/checkVerificationCode.gql'
import getCurrentUser from 'graphql/user/getCurrentUser.gql'

import './user-phone-verification.pcss'

/**
 * Component for verifying user's phone number using OTP.
 */
const UserPhoneVerification = ({
    onSubmitted,
    onCancel
}) => {
    const location = useLocation()
    const primaryLocation = getPrimaryLocation(location)
    const { t, i18n } = useTranslation('editProfile')

    const [otp, setOtp] = useState('')
    const [otpError, setOtpError] = useState(false)
    const [showText, setShowText] = useState(false)

    // Retrieve user's phone number from cookies
    const [userPhoneVerification] = useState(cookies.get('user-phone-verification'))
    const [query] = useState(queryString.parse(primaryLocation.search))

    const [checkPhoneVerificationCode, { loading: checkPhoneVerificationCodeLoading }] = useMutation(checkVerificationCode)
    const phoneNumber = userPhoneVerification && userPhoneVerification.phoneNumber

    useAssert(userPhoneVerification, 'Missing required cookie "userPhoneVerification"')
    useAssert(phoneNumber, 'Missing required param "phoneNumber"')

    // Effect hook to trigger submission when OTP length is 4
    useEffect(() => {
        if (otp.length === 4) {
            handleSubmit()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp])

    // If userPhoneVerification or phoneNumber is missing, call onCancel
    if (!userPhoneVerification && !phoneNumber) {
        onCancel()
    }

    const handleSubmit = async () => {
        await checkPhoneVerificationCode({
            variables: {
                mobileNumber: phoneNumber,
                code: otp
            },
            refetchQueries: [{
                query: getCurrentUser,
            }],
            awaitRefetchQueries: true,
        }).then((result) => {
            const { status } = result.data.checkVerificationCode

            // If verification successful, redirect or call onSubmitted
            if (!status) {
                setOtpError(true)
            } else if (query?.type === REDIRECT_USER_TYPE.MOBILE_VERIFICATION && query?.redirect) {
                let plainTextRedirectUrl = query.redirect

                if (isBase64Encoded(query.redirect)) {
                    plainTextRedirectUrl = window.atob(query.redirect)
                }
                if (isAllowedRedirectUrl(plainTextRedirectUrl)) {
                    window.location.replace(plainTextRedirectUrl)
                } else {
                    onSubmitted()
                    logException('Invalid redirect url: ' + plainTextRedirectUrl)
                }
            } else {
                onSubmitted()
            }
            return Promise.resolve(result)
        }).catch((error) => {
            setOtpError(true)
            return Promise.reject(error)
        })
    }

    return (
        <div className="user-phone-verification">
            <div className="user-phone-verification__content">
                <p>{t('editProfile:verifyPhoneNumber.shortDescription', { phoneNumber: phoneNumber })}</p>
            </div>
            <div className="user-phone-verification__code">
                {otpError && <span className="error-message">{t('editProfile:verifyPhoneNumber.errorMessage')}</span>}
                <OtpInput
                    isInputNum
                    value={otp}
                    onChange={value => setOtp(value)}
                    numInputs={4}
                    hasErrored={otpError}
                    errorStyle="error"
                    inputStyle="inputStyle"
                    renderInput={(props) => <input {...props} />}
                    shouldAutoFocus={true}
                />
            </div>
            {!showText && (
                <div className="user-phone-verification__link">
                    <Button
                        type="button"
                        className="button button--tertiary"
                        trackName="phoneNumber"
                        onClick={e => {
                            e.preventDefault()
                            setShowText(true)
                        }}
                    >
                        {t('editProfile:verifyPhoneNumber.linkText')}
                    </Button>
                </div>
            )}
            {!!showText && (
                <div className="user-phone-verification__bottom-content">
                    <p>{t('editProfile:verifyPhoneNumber.description.paragraph1')}</p>
                    <p>
                        <Trans i18nKey="editProfile:verifyPhoneNumber.description.paragraph2">
                            Ongelmatilanteissa <Link to={`${getLocale(i18n.language)}/ota-yhteytta`} className="button button--tertiary" trackName="customerService">ota yhteyttä asiakaspalveluun.</Link>
                        </Trans>
                    </p>
                </div>
            )}
            <div className="user-phone-verification__action">
                <Button
                    type="button"
                    className="button"
                    trackName="confirm"
                    onClick={handleSubmit}
                    loading={checkPhoneVerificationCodeLoading}
                    disabled={otp.length !== 4 || checkPhoneVerificationCodeLoading}
                >
                    {t('editProfile:verifyPhoneNumber.action.confirm')}
                </Button>
            </div>
        </div>
    )
}

export default UserPhoneVerification
